(function () {
  // VAT rate from Theme settings > Delivery & VAT (set on <html data-vat-rate> in layout/theme.liquid).
  const VAT_RATE = (function () {
    const rate = parseFloat(document.documentElement.getAttribute("data-vat-rate"));
    return Number.isFinite(rate) && rate >= 0 ? rate : 0.20;
  })();
  // The shopper's Ex/Inc VAT choice is remembered on their device (localStorage)
  // so it survives new visits. Older session-only choices are still read once.
  const VAT_SESSION_KEY = "vat_mode_session";
  const VAT_SAVED_KEY = "grp_vat_mode";

  // =========================
  // STORAGE SAFE HELPERS
  // =========================
  function safeGetSession(key) {
    try {
      const saved = localStorage.getItem(VAT_SAVED_KEY);
      if (saved) return saved;
    } catch {}
    try { return sessionStorage.getItem(key); } catch { return null; }
  }

  function safeSetSession(key, value) {
    try { localStorage.setItem(VAT_SAVED_KEY, value); } catch {}
    try { sessionStorage.setItem(key, value); } catch {}
  }

  // =========================
  // MODE
  // =========================
  function getModeDefault() {
    const root = document.documentElement;
    const allowed = root.getAttribute("data-vat-mode-allowed") || "both";
    const def = root.getAttribute("data-vat-default") || "ex";

    if (allowed === "inc") return "inc";
    if (allowed === "ex") return "ex";
    return def === "inc" ? "inc" : "ex";
  }

  function getMode() {
    const root = document.documentElement;
    const current = root.getAttribute("data-vat-mode");

    if (current === "ex" || current === "inc" || current === "both") return current;

    const saved = safeGetSession(VAT_SESSION_KEY);
    if (saved === "ex" || saved === "inc" || saved === "both") return saved;

    return getModeDefault();
  }

  function setMode(mode) {
    const root = document.documentElement;
    const allowed = root.getAttribute("data-vat-mode-allowed") || "both";

    if (allowed !== "both") mode = allowed;
    if (!["ex", "inc", "both"].includes(mode)) mode = getModeDefault();

    safeSetSession(VAT_SESSION_KEY, mode);
    root.setAttribute("data-vat-mode", mode);

    applyVatToAllPrices();
    syncAllToggles();

    window.dispatchEvent(new CustomEvent("vat:modeChanged", { detail: { mode } }));
  }

  // =========================
  // PRICE RENDERING
  // =========================
  function moneyGBPFromCents(cents) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP"
    }).format(cents / 100);
  }

  function computeInc(ex) {
    return Math.round(ex * (1 + VAT_RATE));
  }

  function formatPerM2LabelFromExValue(exValue, mode) {
    if (!Number.isFinite(exValue) || exValue <= 0) return "";

    const incValue = exValue * (1 + VAT_RATE);
    const activeValue = mode === "inc" ? incValue : exValue;

    const roundedUp = Math.ceil(activeValue);
    const isWhole = Math.abs(activeValue - Math.round(activeValue)) < 0.0001;

    if (isWhole) {
      return `£${Math.round(activeValue)} per m²`;
    }

    return `Under £${roundedUp} per m²`;
  }

  /* Same rounding/wording logic as formatPerM2LabelFromExValue, but split
     into parts for the collection-card stacked badge (Under / £X / per m²)
     instead of one flat string - see applyVatToPerM2 below. */
  function formatPerM2PartsFromExValue(exValue, mode) {
    if (!Number.isFinite(exValue) || exValue <= 0) return null;

    const incValue = exValue * (1 + VAT_RATE);
    const activeValue = mode === "inc" ? incValue : exValue;

    const roundedUp = Math.ceil(activeValue);
    const isWhole = Math.abs(activeValue - Math.round(activeValue)) < 0.0001;

    if (isWhole) {
      return { prefix: "", price: `£${Math.round(activeValue)}`, suffix: "per m²" };
    }

    return { prefix: "Under", price: `£${roundedUp}`, suffix: "per m²" };
  }

  function renderPrice(el, mode) {
    const ex = parseInt(el.getAttribute("data-price-cents"), 10);
    if (!Number.isFinite(ex)) return;

    const inc = computeInc(ex);

    // SPECIAL CASE: toolkit upsell → keep original styling, only swap value
    if (el.closest(".grp-toolkit-upsell__price-row")) {
      const value = mode === "inc" ? inc : ex;
      el.textContent = moneyGBPFromCents(value);
      return;
    }

    const isCompare = !!el.closest("[data-vat-compare], s") || el.hasAttribute("data-vat-compare");
    const isSaving = el.hasAttribute("data-vat-saving");
    const hidePill = isCompare || isSaving;

    if (mode === "ex") {
      el.innerHTML = `
        <span class="vat-display vat-display--single">
          <span class="vat-main">${moneyGBPFromCents(ex)}</span>
          ${hidePill ? "" : '<span class="vat-pill">ex. VAT</span>'}
        </span>`;
      return;
    }

    if (mode === "inc") {
      el.innerHTML = `
        <span class="vat-display vat-display--single">
          <span class="vat-main">${moneyGBPFromCents(inc)}</span>
          ${hidePill ? "" : '<span class="vat-pill">inc. VAT</span>'}
        </span>`;
      return;
    }

    // BOTH
    el.innerHTML = `
      <span class="vat-display vat-display--both">
        <span class="vat-main">${moneyGBPFromCents(inc)}</span>
        <span class="vat-sub">
          <span class="vat-sub__price">${moneyGBPFromCents(ex)}</span>
          ${hidePill ? "" : '<span class="vat-pill">ex. VAT</span>'}
        </span>
      </span>`;
  }

  function applyVatToPerM2(root) {
    const mode = getMode();
    const scope = root || document;
    const nodes = scope.querySelectorAll("[data-price-per-m2][data-price-per-m2-ex]");

    nodes.forEach((wrap) => {
      const exValue = parseFloat(wrap.getAttribute("data-price-per-m2-ex"));
      if (!Number.isFinite(exValue) || exValue <= 0) return;

      const label = formatPerM2LabelFromExValue(exValue, mode);

      const link = wrap.querySelector(".frk-card-price-link--m2");
      if (link) {
        link.textContent = label;
      }

      const overlayText = wrap.querySelector(".frk-card-m2-overlay__text");
      if (overlayText) {
        if (overlayText.classList.contains("frk-card-m2-overlay__text--stacked")) {
          /* Collection-grid badge: rebuild the Under / £X / per m² spans
             instead of collapsing to plain text, so the price stays the
             visually dominant element after a VAT-mode toggle. */
          const parts = formatPerM2PartsFromExValue(exValue, mode);
          if (parts) {
            overlayText.innerHTML = "";
            if (parts.prefix) {
              const prefixEl = document.createElement("span");
              prefixEl.className = "frk-card-m2-overlay__prefix";
              prefixEl.textContent = parts.prefix;
              overlayText.appendChild(prefixEl);
            }
            const priceEl = document.createElement("span");
            priceEl.className = "frk-card-m2-overlay__price";
            priceEl.textContent = parts.price;
            overlayText.appendChild(priceEl);
            const suffixEl = document.createElement("span");
            suffixEl.className = "frk-card-m2-overlay__suffix";
            suffixEl.textContent = parts.suffix;
            overlayText.appendChild(suffixEl);
          }
        } else {
          overlayText.textContent = label;
        }
      }
    });
  }

    function applyVatToPerMetre(root) {
    const mode = getMode();
    const scope = root || document;
    const nodes = scope.querySelectorAll("[data-price-per-metre][data-price-per-metre-ex]");

    nodes.forEach((wrap) => {
      const exValue = parseFloat(wrap.getAttribute("data-price-per-metre-ex"));
      if (!Number.isFinite(exValue) || exValue <= 0) return;

      const activeValue = mode === "inc" ? exValue * (1 + VAT_RATE) : exValue;
      wrap.textContent = `£${activeValue.toFixed(2)} / metre`;
    });
  }

  function applyVatToAllPrices(root) {
    const mode = getMode();
    document.documentElement.setAttribute("data-vat-mode", mode);

    const scope = root || document;
    const nodes = scope.querySelectorAll("[data-vat-price][data-price-cents]");
    nodes.forEach(el => renderPrice(el, mode));

    applyVatToPerM2(scope);
    applyVatToPerMetre(scope);
  }

  // =========================
  // TOGGLE SYSTEM
  // =========================
  function syncAllToggles() {
    const mode = getMode();

    document.querySelectorAll("[data-vat-toggle-input]").forEach(input => {
      input.checked = mode === "inc";
    });
  }

  document.addEventListener("change", function (e) {
    const input = e.target.closest("[data-vat-toggle-input]");
    if (!input) return;

    const mode = input.checked ? "inc" : "ex";
    setMode(mode);
  });

  // =========================
  // AUTO DOM WATCH
  // =========================
  // New content (cart drawer, quick view, AJAX filtering, predictive search)
  // is picked up here. Added elements are collected and handled once per
  // animation frame, and changes made by renderPrice() itself are ignored,
  // so busy pages (carousels, apps) don't trigger repeated work.
  const PRICE_SEL = "[data-vat-price][data-price-cents]";
  const M2_SEL = "[data-price-per-m2][data-price-per-m2-ex]";
  const METRE_SEL = "[data-price-per-metre][data-price-per-metre-ex]";
  const TOGGLE_SEL = "[data-vat-toggle-input]";
  const ANY_SEL = [PRICE_SEL, M2_SEL, METRE_SEL, TOGGLE_SEL].join(",");

  function observeDomChanges() {
    const pending = new Set();
    let scheduled = false;

    function flush() {
      scheduled = false;
      const nodes = Array.from(pending);
      pending.clear();
      const mode = getMode();
      let toggles = false;

      nodes.forEach((node) => {
        if (!node.isConnected) return;
        // Skip nodes that sit inside another queued node (handled with it)
        for (let p = node.parentElement; p; p = p.parentElement) {
          if (pending.has(p) || nodes.includes(p)) return;
        }
        if (node.matches(PRICE_SEL)) renderPrice(node, mode);
        node.querySelectorAll(PRICE_SEL).forEach((el) => renderPrice(el, mode));
        if (node.matches(M2_SEL) || node.querySelector(M2_SEL)) applyVatToPerM2(node.parentElement || node);
        if (node.matches(METRE_SEL) || node.querySelector(METRE_SEL)) applyVatToPerMetre(node.parentElement || node);
        if (node.matches(TOGGLE_SEL) || node.querySelector(TOGGLE_SEL)) toggles = true;
      });

      if (toggles) syncAllToggles();
    }

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Our own price rendering replaces the contents of [data-vat-price]
        if (m.target.closest && m.target.closest("[data-vat-price]")) continue;

        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches(ANY_SEL) || node.querySelector(ANY_SEL)) pending.add(node);
        });
      }

      if (pending.size && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // =========================
  // THEME EVENTS
  // =========================
  function hookThemeEvents() {
    const refresh = () => {
      applyVatToAllPrices();
      syncAllToggles();
    };

    document.addEventListener("cart:refresh", refresh);
    document.addEventListener("cart:updated", refresh);
    document.addEventListener("shopify:section:load", refresh);
  }

  // =========================
  // INIT
  // =========================
  document.addEventListener("DOMContentLoaded", function () {
    applyVatToAllPrices();
    syncAllToggles();
    observeDomChanges();
    hookThemeEvents();
  });

  // =========================
  // DEBUG
  // =========================
  window.__VAT = {
    getMode,
    setMode,
    applyVatToAllPrices,
    applyVatToPerM2,
    applyVatToPerMetre
  };
})();